import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  getCryptoRampKycStatus,
  getCryptoRampQuoteSimulation,
  getPostCryptoRampKycInitStreamUrl,
  getPostCryptoRampPixInitStreamUrl,
  postCryptoRampPixOfframp,
} from "../api/endpoints/crypto-ramp/crypto-ramp";
import { getWalletHistoryId } from "../api/endpoints/wallet/wallet";
import type {
  GetCryptoRampKycStatus200Data,
  GetCryptoRampKycStatus200DataBeneficiary,
  GetCryptoRampKycStatus200DataFiatCurrency,
  GetCryptoRampKycStatus200DataLimits,
  GetCryptoRampKycStatus200DataOptions,
  GetCryptoRampKycStatus200DataSubaccount,
  GetCryptoRampQuoteSimulation200Data,
  GetCryptoRampQuoteSimulationParams,
  PostCryptoRampPixInitStream200StreamEnd,
  PostCryptoRampPixInitStream200StreamState,
  WalletHistoryDetail,
} from "../api/models";
import { useSSE } from "../hooks/useSSE";
import { IconSelect } from "../components/IconSelect";
import { Toast } from "../components/Toast";
import "./CryptoRampPage.css";

type Subaccount = NonNullable<GetCryptoRampKycStatus200DataSubaccount>;

// Seleções da simulação: `undefined` significa "a API ainda não ofereceu essa
// opção" — nunca assumimos um default local.
type QuoteInputCurrency =
  | GetCryptoRampQuoteSimulationParams["inputCurrency"]
  | undefined;
type QuoteOutputCurrency =
  | GetCryptoRampQuoteSimulationParams["outputCurrency"]
  | undefined;
type QuotePaymentMethod =
  | GetCryptoRampQuoteSimulationParams["inputPaymentMethod"]
  | undefined;

interface StreamStartData {
  providerName: string;
}

interface StreamEndData {
  subAccountId: string;
  message: string;
}

interface DebugEntry {
  label: string;
  raw: string;
  timestamp: string;
}

export default function CryptoRampPage() {
  const navigate = useNavigate();
  const [kycData, setKycData] = useState<GetCryptoRampKycStatus200Data | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<
    { message: string; type?: "error" | "success" | "info" } | null
  >(null);

  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [debugLogs, setDebugLogs] = useState<DebugEntry[]>([]);
  const [streamComplete, setStreamComplete] = useState(false);
  const [streamCompleteMessage, setStreamCompleteMessage] = useState("");

  const sse = useSSE<StreamStartData, StreamEndData, Subaccount>();

  const loadKycStatus = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getCryptoRampKycStatus();
      if (response.status !== 200) {
        const errorData = response.data as { message?: string };
        throw new Error(
          errorData?.message || `Erro ao buscar status KYC: ${response.status}`,
        );
      }
      setKycData(response.data.data);
    } catch (error: any) {
      console.error("Erro ao carregar status KYC:", error);
      setLoadError(error.message || "Erro ao carregar status");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKycStatus();
  }, [loadKycStatus]);

  const handleStartVerification = () => {
    setProgressPercent(0);
    setProgressMessage("");
    setCompletedSteps([]);
    setDebugLogs([]);
    setStreamComplete(false);
    setStreamCompleteMessage("");

    sse.start({
      url: getPostCryptoRampKycInitStreamUrl(),
      method: "POST",
      onProgress: (data) => {
        setProgressPercent(data.progress);
        setProgressMessage(data.message);
        setCompletedSteps((prev) => {
          if (prev.includes(data.code)) return prev;
          return [...prev, data.code];
        });
      },
      onState: (subaccount) => {
        setKycData((prev) => prev ? { ...prev, subaccount } : null);
        const { status, rejectionReason, rejectionCode } = subaccount;
        setDebugLogs((prev) => [
          ...prev,
          {
            label: "🔄 state",
            raw: JSON.stringify({ status, rejectionReason, rejectionCode }),
            timestamp: new Date().toLocaleTimeString("pt-BR"),
          },
        ]);
      },
      onDebug: (data) => {
        setDebugLogs((prev) => [
          ...prev,
          {
            label: "🐛 debug",
            raw: JSON.stringify(data),
            timestamp: new Date().toLocaleTimeString("pt-BR"),
          },
        ]);
      },
      onEnd: (data) => {
        setProgressPercent(100);
        setStreamComplete(true);
        setStreamCompleteMessage(data.message);
      },
      onError: (error) => {
        setToast({ message: error.message, type: "error" });
      },
    });
  };

  const handleRetry = () => {
    sse.reset();
    handleStartVerification();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const subaccount = kycData?.subaccount;

  // Loading state
  if (isLoading) {
    return (
      <div className="crypto-ramp-page">
        <div className="crypto-ramp-loading">
          <div className="loading-spinner" />
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Load error
  if (loadError) {
    return (
      <div className="crypto-ramp-page">
        <div className="crypto-ramp-error">
          <p>{loadError}</p>
          <button
            type="button"
            className="ramp-primary-button"
            onClick={loadKycStatus}
            style={{ maxWidth: "200px" }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crypto-ramp-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="crypto-ramp-container">
        <div className="crypto-ramp-header">
          <button
            type="button"
            className="crypto-ramp-back"
            onClick={() => navigate("/profile")}
          >
            &#8592; Voltar
          </button>
          <h1>Crypto Ramp</h1>
          <div />
        </div>

        <div className="crypto-ramp-card">
          {/* Streaming in progress */}
          {sse.isStreaming && (
            <div className="ramp-progress-overlay">
              <h2>Iniciando verificação...</h2>
              <div className="ramp-progress-bar-container">
                <div
                  className="ramp-progress-bar-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="ramp-progress-message">
                {progressMessage || "Conectando..."}
              </p>
              <div className="ramp-progress-steps">
                {completedSteps.map((code, idx) => (
                  <div
                    key={code}
                    className={`ramp-progress-step ${
                      idx === completedSteps.length - 1
                        ? "ramp-progress-step--active"
                        : "ramp-progress-step--done"
                    }`}
                  >
                    <span className="ramp-progress-step-icon">
                      {idx === completedSteps.length - 1 ? "⏳" : "✓"}
                    </span>
                    <span>{code}</span>
                  </div>
                ))}
              </div>
              {debugLogs.length > 0 && (
                <div className="ramp-debug-box">
                  <div className="ramp-debug-header">Debug</div>
                  <div className="ramp-debug-entries">
                    {debugLogs.map((log, idx) => (
                      <div key={idx} className="ramp-debug-entry">
                        <span className="ramp-debug-time">{log.timestamp}</span>
                        <span className="ramp-debug-label">{log.label}</span>
                        <span className="ramp-debug-msg">{log.raw}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="ramp-secondary-button"
                onClick={sse.stop}
              >
                Cancelar
              </button>
            </div>
          )}

          {/* Stream completed */}
          {!sse.isStreaming && streamComplete && (
            <div className="ramp-complete-message">
              <div className="ramp-complete-icon">✅</div>
              <h3>Verificação concluída!</h3>
              <p>{streamCompleteMessage}</p>
              {completedSteps.length > 0 && (
                <div className="ramp-progress-steps">
                  {completedSteps.map((code) => (
                    <div
                      key={code}
                      className="ramp-progress-step ramp-progress-step--done"
                    >
                      <span className="ramp-progress-step-icon">✓</span>
                      <span>{code}</span>
                    </div>
                  ))}
                </div>
              )}
              {debugLogs.length > 0 && (
                <div className="ramp-debug-box">
                  <div className="ramp-debug-header">Debug</div>
                  <div className="ramp-debug-entries">
                    {debugLogs.map((log, idx) => (
                      <div key={idx} className="ramp-debug-entry">
                        <span className="ramp-debug-time">{log.timestamp}</span>
                        <span className="ramp-debug-label">{log.label}</span>
                        <span className="ramp-debug-msg">{log.raw}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="ramp-primary-button"
                onClick={() => {
                  sse.reset();
                  setStreamComplete(false);
                  setDebugLogs([]);
                  loadKycStatus();
                }}
              >
                Continuar
              </button>
            </div>
          )}

          {/* Stream error */}
          {!sse.isStreaming && sse.status === "error" && !streamComplete && (
            <div className="ramp-stream-error">
              <div className="ramp-stream-error-icon">⚠️</div>
              <p>{sse.error || "Erro ao processar verificação."}</p>
              <button
                type="button"
                className="ramp-primary-button"
                onClick={handleRetry}
              >
                Tentar novamente
              </button>
              <button
                type="button"
                className="ramp-secondary-button"
                onClick={() => {
                  sse.reset();
                }}
              >
                Voltar
              </button>
            </div>
          )}

          {/* Normal states (not streaming) */}
          {!sse.isStreaming &&
            sse.status !== "error" &&
            !streamComplete &&
            renderSubaccountState(
              subaccount,
              handleStartVerification,
              formatDate,
            )}
        </div>

        {/* Fiat currency card */}
        {kycData?.fiatCurrency && (
          <div className="crypto-ramp-card" style={{ marginTop: "1.5rem" }}>
            <FiatCurrencyCard currency={kycData.fiatCurrency} />
          </div>
        )}

        {/* Limits card */}
        {kycData?.limits && (
          <div className="crypto-ramp-card" style={{ marginTop: "1.5rem" }}>
            <LimitsCard
              limits={kycData.limits}
              currencySymbol={kycData.fiatCurrency?.symbol}
            />
          </div>
        )}

        {/* Bank / beneficiary info card */}
        <div className="crypto-ramp-card" style={{ marginTop: "1.5rem" }}>
          <BeneficiaryCard
            beneficiary={kycData?.beneficiary ?? null}
            formatDate={formatDate}
          />
        </div>

        {/* Quote Simulation - always visible */}
        <div className="crypto-ramp-card" style={{ marginTop: "1.5rem" }}>
          <QuoteSimulationForm
            options={kycData?.options ?? null}
            fiatCurrency={kycData?.fiatCurrency ?? null}
          />
        </div>
      </div>
    </div>
  );
}

interface PixStartData {
  provider: string;
}

const OFFRAMP_TERMINAL_STATUSES: WalletHistoryDetail["status"][] = [
  "CONFIRMED",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
];

function isOfframpTerminal(status: WalletHistoryDetail["status"]) {
  return OFFRAMP_TERMINAL_STATUSES.includes(status);
}

function getWalletStatusLabel(status: WalletHistoryDetail["status"]) {
  const labels: Record<WalletHistoryDetail["status"], string> = {
    CREATED: "Criado",
    AWAITING_PAYMENT: "Processando",
    PROCESSING: "Processando",
    CONFIRMED: "Concluído",
    FAILED: "Falhou",
    EXPIRED: "Expirado",
    CANCELLED: "Cancelado",
  };
  return labels[status] ?? status;
}

function getWalletStatusVariant(status: WalletHistoryDetail["status"]) {
  if (status === "CONFIRMED") return "activated";
  if (status === "FAILED" || status === "EXPIRED" || status === "CANCELLED") {
    return "rejected";
  }
  return "pending";
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("pt-BR");
}

function mapOfframpError(code: string | undefined, fallback: string | undefined) {
  switch (code) {
    case "E_QUOTE_EXPIRED":
    case "E_QUOTE_INVALID":
      return "A cotação expirou. Refaça a simulação.";
    case "E_SUBACCOUNT_NOT_FOUND":
    case "E_SUBACCOUNT_NOT_ACTIVATED":
      return "Sua verificação (KYC) ainda não foi concluída.";
    case "E_BENEFICIARY_REQUIRED":
      return "Não foi possível resolver seu beneficiário PIX. Verifique se o CPF está no seu perfil.";
    default:
      return fallback || "Não foi possível confirmar o saque.";
  }
}

function QuoteSimulationForm({
  options,
  fiatCurrency,
}: {
  // `kycData` pode não ter chegado ainda — o corpo já trata a ausência e
  // mostra "indisponível" em vez de inventar os pares.
  options: GetCryptoRampKycStatus200DataOptions | null;
  fiatCurrency: GetCryptoRampKycStatus200DataFiatCurrency;
}) {
  const [rampType, setRampType] = useState<
    GetCryptoRampQuoteSimulationParams["type"]
  >("ON_RAMP");
  const [inputCurrency, setInputCurrency] = useState<QuoteInputCurrency>(
    options?.onramp.input[0]?.code as QuoteInputCurrency,
  );
  const [outputCurrency, setOutputCurrency] = useState<QuoteOutputCurrency>(
    options?.onramp.output[0]?.code as QuoteOutputCurrency,
  );
  const [paymentMethod, setPaymentMethod] = useState<QuotePaymentMethod>(
    options?.onramp.paymentMethods[0]?.code as QuotePaymentMethod,
  );

  const activeOptions = rampType === "ON_RAMP"
    ? options?.onramp
    : options?.offramp;

  // A API é a única fonte dos pares negociáveis. Se o país do usuário não
  // expõe moedas/métodos, não há o que simular — a tela mostra indisponível em
  // vez de inventar BRL/PIX/USDT.
  const availableOptions = activeOptions &&
      activeOptions.input.length > 0 &&
      activeOptions.output.length > 0 &&
      activeOptions.paymentMethods.length > 0
    ? activeOptions
    : null;

  const [inputAmount, setInputAmount] = useState("");
  const [outputAmount, setOutputAmount] = useState("");
  const lastEditedRef = useRef<"input" | "output">("input");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updatingFromApiRef = useRef(false);
  const [quote, setQuote] = useState<
    GetCryptoRampQuoteSimulation200Data | null
  >(
    null,
  );
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchQuoteRef = useRef<() => void>(() => {});

  // PIX modal state
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [pixProgress, setPixProgress] = useState(0);
  const [pixProgressMessage, setPixProgressMessage] = useState("");
  const [pixSteps, setPixSteps] = useState<string[]>([]);
  const [pixState, setPixState] = useState<
    PostCryptoRampPixInitStream200StreamState | null
  >(null);
  const [pixEnd, setPixEnd] = useState<
    PostCryptoRampPixInitStream200StreamEnd | null
  >(null);
  const [pixCountdown, setPixCountdown] = useState<number | null>(null);
  const [pixCopied, setPixCopied] = useState(false);
  const pixCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pixSse = useSSE<
    PixStartData,
    PostCryptoRampPixInitStream200StreamEnd,
    PostCryptoRampPixInitStream200StreamState
  >();

  // Off-ramp (saque) modal state
  const [offrampModalOpen, setOfframpModalOpen] = useState(false);
  const [offrampDetail, setOfframpDetail] = useState<WalletHistoryDetail | null>(
    null,
  );
  const [offrampError, setOfframpError] = useState<string | null>(null);
  const [offrampTimedOut, setOfframpTimedOut] = useState(false);
  const offrampPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const offrampTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopOfframpPolling = useCallback(() => {
    if (offrampPollRef.current) {
      clearInterval(offrampPollRef.current);
      offrampPollRef.current = null;
    }
    if (offrampTimeoutRef.current) {
      clearTimeout(offrampTimeoutRef.current);
      offrampTimeoutRef.current = null;
    }
  }, []);

  const startOfframpPolling = useCallback((id: string) => {
    stopOfframpPolling();

    offrampTimeoutRef.current = setTimeout(() => {
      stopOfframpPolling();
      setOfframpTimedOut(true);
    }, 180000);

    offrampPollRef.current = setInterval(async () => {
      try {
        const res = await getWalletHistoryId(id);
        if (res.status === 200) {
          const fresh = res.data.data;
          setOfframpDetail(fresh);
          if (isOfframpTerminal(fresh.status)) {
            stopOfframpPolling();
          }
        } else if (res.status === 404) {
          stopOfframpPolling();
          setOfframpError("Operação não encontrada.");
        }
      } catch {
        // erro de rede transitório: mantém o polling até o timeout
      }
    }, 4000);
  }, [stopOfframpPolling]);

  const handleWithdraw = useCallback(async () => {
    if (!quote?.quoteId) return;

    setOfframpModalOpen(true);
    setOfframpDetail(null);
    setOfframpError(null);
    setOfframpTimedOut(false);

    try {
      const response = await postCryptoRampPixOfframp({
        quoteId: quote.quoteId,
      });
      if (response.status !== 201) {
        const errData = response.data as {
          message?: string;
          error?: { code?: string };
        };
        throw new Error(
          mapOfframpError(errData?.error?.code, errData?.message),
        );
      }
      const detail = response.data.data;
      setOfframpDetail(detail);
      if (!isOfframpTerminal(detail.status)) {
        startOfframpPolling(detail.id);
      }
    } catch (e) {
      setOfframpError(
        e instanceof Error ? e.message : "Erro ao confirmar saque",
      );
    }
  }, [quote?.quoteId, startOfframpPolling]);

  const handleCloseOfframpModal = useCallback(() => {
    stopOfframpPolling();
    setOfframpModalOpen(false);
  }, [stopOfframpPolling]);

  useEffect(() => stopOfframpPolling, [stopOfframpPolling]);

  const clearTimers = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  // PIX countdown
  useEffect(() => {
    if (!pixState?.expiresAt) return;
    const expiresAt = new Date(pixState.expiresAt).getTime();

    const update = () => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );
      setPixCountdown(remaining);
      return remaining;
    };
    update();

    pixCountdownRef.current = setInterval(() => {
      if (update() <= 0 && pixCountdownRef.current) {
        clearInterval(pixCountdownRef.current);
        pixCountdownRef.current = null;
      }
    }, 1000);

    return () => {
      if (pixCountdownRef.current) {
        clearInterval(pixCountdownRef.current);
        pixCountdownRef.current = null;
      }
    };
  }, [pixState?.expiresAt]);

  // onramp e offramp expõem moedas diferentes: ao trocar de tab, reposiciona
  // as seleções para os defaults do conjunto ativo — e as limpa quando esse
  // conjunto vem vazio.
  useEffect(() => {
    const opts = rampType === "ON_RAMP" ? options?.onramp : options?.offramp;
    setInputCurrency(opts?.input[0]?.code as QuoteInputCurrency);
    setOutputCurrency(opts?.output[0]?.code as QuoteOutputCurrency);
    setPaymentMethod(opts?.paymentMethods[0]?.code as QuotePaymentMethod);
  }, [rampType, options]);

  const handleDeposit = useCallback(() => {
    if (!quote?.quoteId) return;

    setPixModalOpen(true);
    setPixProgress(0);
    setPixProgressMessage("");
    setPixSteps([]);
    setPixState(null);
    setPixEnd(null);
    setPixCountdown(null);
    setPixCopied(false);

    pixSse.start({
      url: getPostCryptoRampPixInitStreamUrl(),
      method: "POST",
      body: { quoteId: quote.quoteId },
      onProgress: (data) => {
        setPixProgress(data.progress);
        setPixProgressMessage(data.message);
        setPixSteps((prev) => {
          if (prev.includes(data.code)) return prev;
          return [...prev, data.code];
        });
      },
      onState: (state) => {
        setPixState(state);
      },
      onEnd: (data) => {
        setPixEnd(data);
        setPixProgress(100);
      },
      onError: () => {},
    });
  }, [quote?.quoteId, pixSse]);

  const handleClosePixModal = useCallback(() => {
    pixSse.stop();
    setPixModalOpen(false);
    if (pixCountdownRef.current) {
      clearInterval(pixCountdownRef.current);
      pixCountdownRef.current = null;
    }
  }, [pixSse]);

  const handleCopyPix = useCallback(async () => {
    if (!pixState?.codePix) return;
    try {
      await navigator.clipboard.writeText(pixState.codePix);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 2000);
    } catch {
      // fallback
    }
  }, [pixState?.codePix]);

  const formatPixCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      TICKET_CREATED: "PIX gerado",
      TICKET_EXPIRED: "PIX expirado",
      DEPOSIT_PROCESSING: "Processando deposito",
      DEPOSIT_SUCCESS: "Deposito confirmado",
      DEPOSIT_FAILED: "Deposito falhou",
      DELIVERY_PROCESSING: "Enviando crypto",
      DELIVERY_SUCCESS: "Crypto enviada",
      DELIVERY_FAILED: "Envio falhou",
      TICKET_COMPLETE: "Transacao concluida",
    };
    return labels[status] || status;
  };

  const startCountdown = useCallback((expiresIn: string) => {
    const expiresAt = new Date(expiresIn).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setCountdown(remaining);
      return remaining;
    };

    updateCountdown();

    countdownRef.current = setInterval(() => {
      const remaining = updateCountdown();
      if (remaining <= 0) {
        clearTimers();
      }
    }, 1000);

    // Auto-refresh 3 seconds before expiration
    const msUntilRefresh = expiresAt - Date.now() - 3000;
    if (msUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        fetchQuoteRef.current();
      }, msUntilRefresh);
    }
  }, [clearTimers]);

  const fetchQuote = useCallback(async () => {
    if (!inputCurrency || !outputCurrency || !paymentMethod) return;

    const activeAmount = lastEditedRef.current === "input"
      ? inputAmount
      : outputAmount;
    if (!activeAmount || Number(activeAmount) <= 0) return;

    setIsLoadingQuote(true);
    setQuoteError(null);
    clearTimers();

    try {
      const params: GetCryptoRampQuoteSimulationParams = {
        type: rampType,
        inputCurrency,
        inputPaymentMethod: paymentMethod,
        outputCurrency,
        ...(lastEditedRef.current === "input"
          ? { inputAmount: activeAmount }
          : { outputAmount: activeAmount }),
      };
      const response = await getCryptoRampQuoteSimulation(params);
      if (response.status !== 200) {
        const errorData = response.data as unknown as { message?: string };
        throw new Error(errorData?.message || "Erro ao buscar cotação");
      }
      const quoteData = response.data.data;
      setQuote(quoteData);
      updatingFromApiRef.current = true;
      if (lastEditedRef.current === "input") {
        setOutputAmount(quoteData.outputAmount);
      } else {
        setInputAmount(quoteData.inputAmount);
      }
      startCountdown(quoteData.quoteExpiresIn);
    } catch (error: any) {
      setQuoteError(error.message || "Erro ao buscar cotação");
      setQuote(null);
    } finally {
      setIsLoadingQuote(false);
    }
  }, [
    rampType,
    inputCurrency,
    outputCurrency,
    paymentMethod,
    inputAmount,
    outputAmount,
    clearTimers,
    startCountdown,
  ]);

  fetchQuoteRef.current = fetchQuote;

  // Debounced auto-fetch when user types
  useEffect(() => {
    if (updatingFromApiRef.current) {
      updatingFromApiRef.current = false;
      return;
    }

    const activeAmount = lastEditedRef.current === "input"
      ? inputAmount
      : outputAmount;
    if (
      !activeAmount || Number(activeAmount) <= 0 || !inputCurrency ||
      !outputCurrency || !paymentMethod
    ) {
      setQuote(null);
      setCountdown(null);
      clearTimers();
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchQuoteRef.current();
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    inputAmount,
    outputAmount,
    rampType,
    inputCurrency,
    outputCurrency,
    paymentMethod,
    clearTimers,
  ]);

  useEffect(() => {
    return () => {
      clearTimers();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [clearTimers]);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="quote-form">
      <h2 className="quote-form-title">Simulação de Cotação</h2>

      <div className="quote-ramp-toggle">
        <button
          type="button"
          className={`quote-ramp-toggle-btn ${
            rampType === "ON_RAMP" ? "quote-ramp-toggle-btn--active" : ""
          }`}
          onClick={() => setRampType("ON_RAMP")}
        >
          Depósito
        </button>
        <button
          type="button"
          className={`quote-ramp-toggle-btn ${
            rampType === "OFF_RAMP" ? "quote-ramp-toggle-btn--active" : ""
          }`}
          onClick={() => setRampType("OFF_RAMP")}
        >
          Saque
        </button>
      </div>

      {!availableOptions
        ? (
          <div className="quote-unavailable">
            <span className="quote-unavailable-icon">🌎</span>
            <p className="quote-unavailable-title">
              {rampType === "ON_RAMP" ? "Depósito" : "Saque"} indisponível
              {fiatCurrency && ` em ${fiatCurrency.name} (${fiatCurrency.code})`}
            </p>
            <p className="quote-unavailable-text">
              Nenhuma moeda ou método de pagamento está habilitado para a sua
              região no momento.
            </p>
          </div>
        )
        : (
          <>
            <div className="quote-form-grid">
              <div className="quote-form-label">
                Moeda Entrada
                <IconSelect
                  value={inputCurrency ?? ""}
                  onChange={(v) =>
                    setInputCurrency(
                      v as GetCryptoRampQuoteSimulationParams["inputCurrency"],
                    )}
                  options={availableOptions.input.map((opt) => ({
                    value: opt.code,
                    label: opt.code,
                    icon: opt.icon,
                  }))}
                />
              </div>

              <div className="quote-form-label">
                Método Pagamento
                <IconSelect
                  value={paymentMethod ?? ""}
                  onChange={(v) =>
                    setPaymentMethod(
                      v as GetCryptoRampQuoteSimulationParams["inputPaymentMethod"],
                    )}
                  options={availableOptions.paymentMethods.map((opt) => ({
                    value: opt.code,
                    label: opt.name,
                  }))}
                />
              </div>

              <div className="quote-form-label">
                Moeda Saída
                <IconSelect
                  value={outputCurrency ?? ""}
                  onChange={(v) =>
                    setOutputCurrency(
                      v as GetCryptoRampQuoteSimulationParams["outputCurrency"],
                    )}
                  options={availableOptions.output.map((opt) => ({
                    value: opt.code,
                    label: opt.code,
                    icon: opt.icon,
                  }))}
                />
              </div>
            </div>

            <div className="quote-amounts-row">
              <label className="quote-form-label">
                Valor de Entrada
                <div
                  className={`quote-amount-input-wrapper ${
                    lastEditedRef.current === "input"
                      ? "quote-amount-input-wrapper--active"
                      : ""
                  }`}
                >
                  <span className="quote-amount-currency">{inputCurrency}</span>
                  <input
                    type="number"
                    className="quote-form-input"
                    value={inputAmount}
                    onChange={(e) => {
                      setInputAmount(e.target.value);
                      lastEditedRef.current = "input";
                    }}
                    placeholder="Ex: 100"
                    min="0"
                    step="any"
                  />
                </div>
              </label>

              <div className="quote-amounts-separator">⇄</div>

              <label className="quote-form-label">
                Valor de Saída
                <div
                  className={`quote-amount-input-wrapper ${
                    lastEditedRef.current === "output"
                      ? "quote-amount-input-wrapper--active"
                      : ""
                  }`}
                >
                  <span className="quote-amount-currency">{outputCurrency}</span>
                  <input
                    type="number"
                    className="quote-form-input"
                    value={outputAmount}
                    onChange={(e) => {
                      setOutputAmount(e.target.value);
                      lastEditedRef.current = "output";
                    }}
                    placeholder="Ex: 20"
                    min="0"
                    step="any"
                  />
                </div>
              </label>
            </div>

            {isLoadingQuote && (
              <div className="quote-loading">Consultando cotação...</div>
            )}

            {quoteError && (
              <div className="quote-error">
                {quoteError}
              </div>
            )}

            {quote && (
              <div className="quote-result">
                <div className="quote-result-header">
                  <span className="quote-result-pair">{quote.pairName}</span>
                  {countdown !== null && (
                    <span
                      className={`quote-countdown ${
                        countdown <= 5 ? "quote-countdown--expiring" : ""
                      }`}
                    >
                      {countdown > 0 ? formatCountdown(countdown) : "Expirado"}
                    </span>
                  )}
                </div>

                <div className="quote-result-main">
                  <div className="quote-result-side">
                    <span className="quote-result-label">Você envia</span>
                    <span className="quote-result-value">
                      {Number(quote.inputAmount).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })} {quote.inputCurrency}
                    </span>
                  </div>
                  <div className="quote-result-arrow">→</div>
                  <div className="quote-result-side">
                    <span className="quote-result-label">Você recebe</span>
                    <span className="quote-result-value">
                      {Number(quote.outputAmount).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })} {quote.outputCurrency}
                    </span>
                  </div>
                </div>

                <div className="quote-result-details">
                  <div className="quote-detail-item">
                    <span>Inclui tarifas</span>
                    <span>
                      {Number(quote.fee).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })} {quote.inputCurrency}
                    </span>
                  </div>
                  <div className="quote-detail-item">
                    <span>Taxa base</span>
                    <span>
                      1.00 {quote.outputCurrency} ={" "}
                      {Number(quote.basePrice).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })} {quote.inputCurrency}
                    </span>
                  </div>
                  <div className="quote-detail-item">
                    <span>Taxa efetiva</span>
                    <span>
                      1.00 {quote.outputCurrency} ={" "}
                      {Number(quote.vetPrice).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })} {quote.inputCurrency}
                    </span>
                  </div>
                  <div className="quote-detail-item">
                    <span>Método</span>
                    <span>{quote.inputPaymentMethod}</span>
                  </div>
                  <div className="quote-detail-item">
                    <span>Provider</span>
                    <span>{quote.provider}</span>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              className="quote-action-btn"
              disabled={!quote || isLoadingQuote}
              onClick={rampType === "ON_RAMP" ? handleDeposit : handleWithdraw}
            >
              {rampType === "ON_RAMP" ? "Depositar" : "Sacar"}
            </button>
          </>
        )}

      {/* PIX Modal */}
      {pixModalOpen && (
        <div className="pix-modal-backdrop" onClick={handleClosePixModal}>
          <div
            className="pix-modal"
            onClick={(e) =>
              e.stopPropagation()}
          >
            <button
              type="button"
              className="pix-modal-close"
              onClick={handleClosePixModal}
            >
              &times;
            </button>

            {/* Terminal state (stream_end received) */}
            {pixEnd
              ? (
                <div className="pix-modal-end">
                  <div className="pix-modal-end-icon">
                    {pixEnd.status === "TICKET_COMPLETE" ||
                        pixEnd.status === "DELIVERY_SUCCESS"
                      ? "✅"
                      : "⚠️"}
                  </div>
                  <h3>{pixEnd.message}</h3>
                  <div className="pix-modal-info-row">
                    <span className="pix-modal-info-label">ID</span>
                    <span className="pix-modal-info-value pix-modal-mono">
                      {pixEnd.transactionId}
                    </span>
                  </div>
                  <div className="pix-modal-info-row">
                    <span className="pix-modal-info-label">Status</span>
                    <span
                      className={`ramp-status-badge ramp-status-badge--${
                        pixEnd.status === "TICKET_COMPLETE" ||
                          pixEnd.status === "DELIVERY_SUCCESS"
                          ? "activated"
                          : "rejected"
                      }`}
                    >
                      {getStatusLabel(pixEnd.status)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ramp-primary-button"
                    onClick={handleClosePixModal}
                    style={{ marginTop: "1.5rem" }}
                  >
                    Fechar
                  </button>
                </div>
              )
              : pixState &&
                  (pixState.status === "DELIVERY_SUCCESS" ||
                    pixState.status === "TICKET_COMPLETE")
              ? (
                /* Transaction confirmed */
                <div className="pix-modal-confirmed">
                  <div className="pix-modal-confirmed-icon">✅</div>
                  <h2 className="pix-modal-title">Transacao confirmada!</h2>

                  <div className="pix-modal-confirmed-summary">
                    <div className="pix-modal-confirmed-amount">
                      <span className="pix-modal-confirmed-label">
                        Voce pagou
                      </span>
                      <span className="pix-modal-confirmed-value">
                        {Number(pixState.inputAmount).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                        })} {pixState.inputCurrency}
                      </span>
                    </div>
                    <div className="pix-modal-confirmed-arrow">→</div>
                    <div className="pix-modal-confirmed-amount">
                      <span className="pix-modal-confirmed-label">
                        Voce recebeu
                      </span>
                      <span className="pix-modal-confirmed-value pix-modal-confirmed-value--highlight">
                        {Number(pixState.outputAmount).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 6,
                        })} {pixState.outputCurrency}
                      </span>
                    </div>
                  </div>

                  <div className="pix-modal-info">
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">ID</span>
                      <span className="pix-modal-info-value pix-modal-mono">
                        {pixState.transactionId}
                      </span>
                    </div>
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">Status</span>
                      <span className="ramp-status-badge ramp-status-badge--activated">
                        {getStatusLabel(pixState.status)}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="ramp-primary-button"
                    onClick={handleClosePixModal}
                    style={{ marginTop: "1.5rem" }}
                  >
                    Fechar
                  </button>
                </div>
              )
              : pixState
              ? (
                /* PIX data received - show QR code */
                <div className="pix-modal-content">
                  <h2 className="pix-modal-title">Pague com PIX</h2>

                  {/* Status badge */}
                  <div className="pix-modal-status-row">
                    <span
                      className={`ramp-status-badge ramp-status-badge--${
                        pixState.status === "TICKET_CREATED"
                          ? "pending"
                          : pixState.status === "DEPOSIT_SUCCESS"
                          ? "activated"
                          : pixState.status === "TICKET_EXPIRED" ||
                              pixState.status === "DEPOSIT_FAILED" ||
                              pixState.status === "DELIVERY_FAILED"
                          ? "rejected"
                          : "pending"
                      }`}
                    >
                      {getStatusLabel(pixState.status)}
                    </span>
                    {pixCountdown !== null && pixCountdown > 0 && (
                      <span
                        className={`quote-countdown ${
                          pixCountdown <= 30 ? "quote-countdown--expiring" : ""
                        }`}
                      >
                        {formatPixCountdown(pixCountdown)}
                      </span>
                    )}
                    {pixCountdown !== null && pixCountdown <= 0 &&
                      pixState.status === "TICKET_CREATED" && (
                      <span className="quote-countdown quote-countdown--expiring">
                        Expirado
                      </span>
                    )}
                  </div>

                  {/* QR Code */}
                  <div className="pix-modal-qr">
                    <QRCodeSVG
                      value={pixState.codePix}
                      size={220}
                      level="M"
                      includeMargin
                    />
                  </div>

                  {/* Transaction info */}
                  <div className="pix-modal-info">
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">ID</span>
                      <span className="pix-modal-info-value pix-modal-mono">
                        {pixState.transactionId}
                      </span>
                    </div>
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">Voce paga</span>
                      <span className="pix-modal-info-value">
                        {Number(pixState.inputAmount).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                        })} {pixState.inputCurrency}
                      </span>
                    </div>
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">Voce recebe</span>
                      <span className="pix-modal-info-value">
                        {Number(pixState.outputAmount).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 6,
                        })} {pixState.outputCurrency}
                      </span>
                    </div>
                  </div>

                  {/* Copia e Cola */}
                  <div className="pix-modal-copy">
                    <label className="pix-modal-copy-label">
                      PIX Copia e Cola
                    </label>
                    <div className="pix-modal-copy-row">
                      <input
                        type="text"
                        readOnly
                        value={pixState.codePix}
                        className="pix-modal-copy-input"
                      />
                      <button
                        type="button"
                        className="pix-modal-copy-btn"
                        onClick={handleCopyPix}
                      >
                        {pixCopied ? "Copiado!" : "Copiar"}
                      </button>
                    </div>
                  </div>

                  {/* Processing indicator */}
                  {pixSse.isStreaming && pixState.status !== "TICKET_CREATED" &&
                    (
                      <div className="pix-modal-processing">
                        <div className="loading-spinner pix-modal-spinner" />
                        <span>{getStatusLabel(pixState.status)}...</span>
                      </div>
                    )}
                </div>
              )
              : (
                /* Loading / progress phase */
                <div className="pix-modal-loading">
                  <h2 className="pix-modal-title">
                    Iniciando transacao PIX...
                  </h2>
                  <div className="ramp-progress-bar-container">
                    <div
                      className="ramp-progress-bar-fill"
                      style={{ width: `${pixProgress}%` }}
                    />
                  </div>
                  <p className="ramp-progress-message">
                    {pixProgressMessage || "Conectando..."}
                  </p>
                  <div className="ramp-progress-steps">
                    {pixSteps.map((code, idx) => (
                      <div
                        key={code}
                        className={`ramp-progress-step ${
                          idx === pixSteps.length - 1
                            ? "ramp-progress-step--active"
                            : "ramp-progress-step--done"
                        }`}
                      >
                        <span className="ramp-progress-step-icon">
                          {idx === pixSteps.length - 1 ? "⏳" : "✓"}
                        </span>
                        <span>{code}</span>
                      </div>
                    ))}
                  </div>

                  {/* SSE error */}
                  {pixSse.status === "error" && (
                    <div className="pix-modal-error">
                      <p>{pixSse.error || pixProgressMessage}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    className="ramp-secondary-button"
                    onClick={handleClosePixModal}
                  >
                    Cancelar
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Off-ramp (saque) Modal */}
      {offrampModalOpen && (
        <div className="pix-modal-backdrop" onClick={handleCloseOfframpModal}>
          <div className="pix-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pix-modal-close"
              onClick={handleCloseOfframpModal}
            >
              &times;
            </button>

            {offrampError
              ? (
                <div className="pix-modal-end">
                  <div className="pix-modal-end-icon">⚠️</div>
                  <h3>{offrampError}</h3>
                  <button
                    type="button"
                    className="ramp-primary-button"
                    onClick={handleWithdraw}
                  >
                    Tentar novamente
                  </button>
                  <button
                    type="button"
                    className="ramp-secondary-button"
                    onClick={handleCloseOfframpModal}
                  >
                    Fechar
                  </button>
                </div>
              )
              : offrampDetail
              ? (
                <div className="pix-modal-content">
                  <h2 className="pix-modal-title">
                    {offrampDetail.status === "CONFIRMED"
                      ? "Saque concluído!"
                      : "Saque via PIX"}
                  </h2>

                  <div className="pix-modal-status-row">
                    <span
                      className={`ramp-status-badge ramp-status-badge--${
                        getWalletStatusVariant(offrampDetail.status)
                      }`}
                    >
                      {getWalletStatusLabel(offrampDetail.status)}
                    </span>
                  </div>

                  <div className="pix-modal-confirmed-summary">
                    <div className="pix-modal-confirmed-amount">
                      <span className="pix-modal-confirmed-label">
                        Você envia
                      </span>
                      <span className="pix-modal-confirmed-value">
                        {Number(offrampDetail.amount).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 6,
                        })} {offrampDetail.coin}
                      </span>
                    </div>
                    <div className="pix-modal-confirmed-arrow">→</div>
                    <div className="pix-modal-confirmed-amount">
                      <span className="pix-modal-confirmed-label">
                        Você recebe
                      </span>
                      <span className="pix-modal-confirmed-value pix-modal-confirmed-value--highlight">
                        {offrampDetail.fiatAmount
                          ? Number(offrampDetail.fiatAmount).toLocaleString(
                            "pt-BR",
                            { minimumFractionDigits: 2 },
                          )
                          : "-"} {offrampDetail.fiatCurrency ?? ""}
                      </span>
                    </div>
                  </div>

                  <div className="pix-modal-info">
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">ID</span>
                      <span className="pix-modal-info-value pix-modal-mono">
                        {offrampDetail.id}
                      </span>
                    </div>
                    {offrampDetail.rampPaymentMethod && (
                      <div className="pix-modal-info-row">
                        <span className="pix-modal-info-label">Método</span>
                        <span className="pix-modal-info-value">
                          {offrampDetail.rampPaymentMethod}
                        </span>
                      </div>
                    )}
                    {offrampDetail.fee && (
                      <div className="pix-modal-info-row">
                        <span className="pix-modal-info-label">Taxa</span>
                        <span className="pix-modal-info-value">
                          {Number(offrampDetail.fee).toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 6,
                          })} {offrampDetail.feeCoin ?? ""}
                        </span>
                      </div>
                    )}
                    <div className="pix-modal-info-row">
                      <span className="pix-modal-info-label">Criado em</span>
                      <span className="pix-modal-info-value">
                        {formatDateTime(offrampDetail.createdAt)}
                      </span>
                    </div>
                    {offrampDetail.confirmedAt && (
                      <div className="pix-modal-info-row">
                        <span className="pix-modal-info-label">
                          Confirmado em
                        </span>
                        <span className="pix-modal-info-value">
                          {formatDateTime(offrampDetail.confirmedAt)}
                        </span>
                      </div>
                    )}
                  </div>

                  {!isOfframpTerminal(offrampDetail.status) &&
                    !offrampTimedOut && (
                    <div className="pix-modal-processing">
                      <div className="loading-spinner pix-modal-spinner" />
                      <span>{getWalletStatusLabel(offrampDetail.status)}...</span>
                    </div>
                  )}

                  {offrampTimedOut &&
                    !isOfframpTerminal(offrampDetail.status) && (
                    <div className="pix-modal-error">
                      <p>
                        Está demorando mais que o esperado. Acompanhe pelo
                        histórico.
                      </p>
                    </div>
                  )}

                  {(isOfframpTerminal(offrampDetail.status) ||
                    offrampTimedOut) && (
                    <button
                      type="button"
                      className="ramp-primary-button"
                      onClick={handleCloseOfframpModal}
                      style={{ marginTop: "1.25rem" }}
                    >
                      Fechar
                    </button>
                  )}
                </div>
              )
              : (
                <div className="pix-modal-loading">
                  <h2 className="pix-modal-title">Confirmando saque...</h2>
                  <div
                    className="pix-modal-processing"
                    style={{ justifyContent: "center" }}
                  >
                    <div className="loading-spinner pix-modal-spinner" />
                    <span>Aguarde...</span>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function renderSubaccountState(
  subaccount: Subaccount | null | undefined,
  onStart: () => void,
  formatDate: (d: string | null) => string,
) {
  // No subaccount
  if (!subaccount) {
    return (
      <div className="ramp-empty-state">
        <div className="ramp-empty-icon">🔐</div>
        <h2>Verificação necessária</h2>
        <p>
          Para comprar e vender crypto via PIX, precisamos verificar sua
          identidade. O processo é rápido e seguro.
        </p>
        <button
          type="button"
          className="ramp-primary-button"
          onClick={onStart}
        >
          Iniciar Verificação
        </button>
      </div>
    );
  }

  // PENDING
  if (subaccount.status === "PENDING") {
    return (
      <div className="ramp-subaccount-info">
        <h2>Subconta</h2>
        <SubaccountDetails subaccount={subaccount} formatDate={formatDate} />
        <div className="ramp-pending-banner">
          <span>⏳</span>
          <p>
            Sua verificação está em andamento. Aguarde a aprovação para começar
            a operar.
          </p>
        </div>
        <button
          type="button"
          className="ramp-primary-button"
          onClick={onStart}
          style={{ marginTop: "1.25rem" }}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // ACTIVATED
  if (subaccount.status === "ACTIVATED") {
    return (
      <div className="ramp-subaccount-info">
        <h2>Subconta</h2>
        <SubaccountDetails subaccount={subaccount} formatDate={formatDate} />
        <div className="ramp-activated-banner">
          <span>✅</span>
          <p>
            Sua conta está ativa! Você já pode realizar operações de compra e
            venda via PIX.
          </p>
        </div>
      </div>
    );
  }

  // REJECTED
  if (subaccount.status === "REJECTED") {
    return (
      <div className="ramp-subaccount-info">
        <h2>Subconta</h2>
        <SubaccountDetails subaccount={subaccount} formatDate={formatDate} />
        <div className="ramp-rejected-banner">
          <div className="ramp-rejected-title">
            <span>❌</span>
            <span>Verificação rejeitada</span>
          </div>
          {subaccount.rejectionCode && (
            <p className="ramp-rejected-code">
              Código: {subaccount.rejectionCode}
            </p>
          )}
          {subaccount.rejectionReason && (
            <p className="ramp-rejected-reason">
              Motivo: {subaccount.rejectionReason}
            </p>
          )}
        </div>
        <button
          type="button"
          className="ramp-primary-button"
          onClick={onStart}
          style={{ marginTop: "1.25rem" }}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return null;
}

function FiatCurrencyCard({
  currency,
}: {
  currency: NonNullable<GetCryptoRampKycStatus200DataFiatCurrency>;
}) {
  return (
    <div className="ramp-fiat-card">
      <div className="ramp-fiat-header">
        {currency.icon && (
          <img
            className="ramp-fiat-icon"
            src={currency.icon}
            alt={currency.code}
          />
        )}
        <div>
          <h2 className="ramp-fiat-title">{currency.name}</h2>
          <span className="ramp-fiat-subtitle">
            {currency.symbol} {currency.code} · {currency.country}
          </span>
        </div>
      </div>
    </div>
  );
}

function LimitsCard({
  limits,
  currencySymbol,
}: {
  limits: NonNullable<GetCryptoRampKycStatus200DataLimits>;
  currencySymbol?: string;
}) {
  // Sem símbolo conhecido, mostra só o número em vez de assumir "R$".
  const formatCurrency = (value: string) => {
    const amount = Number(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currencySymbol ? `${currencySymbol} ${amount}` : amount;
  };

  const renderLimitSection = (
    label: string,
    data: { min: string; max: string; used: string },
  ) => {
    const max = Number(data.max);
    const used = Number(data.used);
    const available = max - used;
    const usedPercent = max > 0 ? (used / max) * 100 : 0;

    return (
      <div className="ramp-limit-section">
        <div className="ramp-limit-header">
          <span className="ramp-limit-label">{label}</span>
          <span className="ramp-limit-available">
            Disponível: {formatCurrency(String(available))}
          </span>
        </div>
        <div className="ramp-limit-bar-container">
          <div
            className={`ramp-limit-bar-fill ${
              usedPercent >= 90 ? "ramp-limit-bar-fill--warning" : ""
            }`}
            style={{ width: `${Math.min(usedPercent, 100)}%` }}
          />
        </div>
        <div className="ramp-limit-details">
          <span>Min: {formatCurrency(data.min)}</span>
          <span>
            Usado: {formatCurrency(data.used)} / {formatCurrency(data.max)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="ramp-limits-card">
      <h2 className="ramp-limits-title">Limites Operacionais</h2>
      {renderLimitSection("Depósito (Onramp)", limits.onramp)}
      {renderLimitSection("Saque (Offramp)", limits.offramp)}
    </div>
  );
}

function BeneficiaryCard({
  beneficiary,
  formatDate,
}: {
  beneficiary: GetCryptoRampKycStatus200DataBeneficiary;
  formatDate: (d: string | null) => string;
}) {
  if (!beneficiary) {
    return (
      <div className="ramp-beneficiary-empty">
        <div className="ramp-beneficiary-empty-icon">🏦</div>
        <h2 className="ramp-beneficiary-title">Dados Bancários</h2>
        <p className="ramp-beneficiary-empty-text">
          Ainda não há dados bancários vinculados à sua conta. Eles aparecerão
          aqui assim que o beneficiário for criado no provider.
        </p>
      </div>
    );
  }

  const statusLabels: Record<string, string> = {
    PENDING: "Pendente",
    ACTIVATED: "Ativo",
    REJECTED: "Rejeitado",
  };
  const statusClass = beneficiary.status.toLowerCase();

  return (
    <div className="ramp-beneficiary-card">
      <div className="ramp-beneficiary-header">
        <h2 className="ramp-beneficiary-title">Dados Bancários</h2>
        <span className={`ramp-status-badge ramp-status-badge--${statusClass}`}>
          {statusLabels[beneficiary.status] ?? beneficiary.status}
        </span>
      </div>

      <div className="ramp-info-grid">
        {beneficiary.holderName && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">Titular</span>
            <span className="ramp-info-value">{beneficiary.holderName}</span>
          </div>
        )}
        {beneficiary.bankName && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">Banco</span>
            <span className="ramp-info-value">{beneficiary.bankName}</span>
          </div>
        )}
        {beneficiary.pixKey && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">Chave PIX</span>
            <span className="ramp-info-value">{beneficiary.pixKey}</span>
          </div>
        )}
        {beneficiary.taxId && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">CPF/CNPJ</span>
            <span className="ramp-info-value">{beneficiary.taxId}</span>
          </div>
        )}
        <div className="ramp-info-item">
          <span className="ramp-info-label">Tipo</span>
          <span className="ramp-info-value">{beneficiary.type}</span>
        </div>
        <div className="ramp-info-item">
          <span className="ramp-info-label">Criado em</span>
          <span className="ramp-info-value">
            {formatDate(beneficiary.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function SubaccountDetails({
  subaccount,
  formatDate,
}: {
  subaccount: Subaccount;
  formatDate: (d: string | null) => string;
}) {
  const statusClass = subaccount.status.toLowerCase();

  return (
    <>
      <div className="ramp-info-grid">
        <div className="ramp-info-item">
          <span className="ramp-info-label">Status</span>
          <span
            className={`ramp-status-badge ramp-status-badge--${statusClass}`}
          >
            {subaccount.status}
          </span>
        </div>
        <div className="ramp-info-item">
          <span className="ramp-info-label">Provider</span>
          <span className="ramp-info-value">{subaccount.rampProvider}</span>
        </div>
        <div className="ramp-info-item">
          <span className="ramp-info-label">País</span>
          <span className="ramp-info-value">{subaccount.country}</span>
        </div>
        <div className="ramp-info-item">
          <span className="ramp-info-label">Criada em</span>
          <span className="ramp-info-value">
            {formatDate(subaccount.createdAt)}
          </span>
        </div>
        {subaccount.approvedAt && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">Aprovada em</span>
            <span className="ramp-info-value">
              {formatDate(subaccount.approvedAt)}
            </span>
          </div>
        )}
        {subaccount.rejectedAt && (
          <div className="ramp-info-item">
            <span className="ramp-info-label">Rejeitada em</span>
            <span className="ramp-info-value">
              {formatDate(subaccount.rejectedAt)}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
